/**
 * Vue 3-compatible stand-in for vue-clipboard2.
 * Supports the legacy APIs still used across the app:
 *   v-clipboard:copy / v-clipboard:success / v-clipboard:error
 *   this.$copyText(text)
 */

function fallbackCopy(text) {
	const el = document.createElement("textarea");
	el.value = text;
	el.setAttribute("readonly", "");
	el.style.position = "fixed";
	el.style.top = "-9999px";
	el.style.left = "-9999px";
	document.body.appendChild(el);
	el.select();
	const ok = document.execCommand("copy");
	document.body.removeChild(el);
	if (!ok) {
		throw new Error("execCommand copy failed");
	}
}

async function copyText(text) {
	const value = text == null ? "" : String(text);
	if (navigator.clipboard?.writeText) {
		await navigator.clipboard.writeText(value);
	} else {
		fallbackCopy(value);
	}
	return { text: value, action: "copy" };
}

function resolveHandler(binding) {
	const value = binding.value;
	if (typeof value === "function") return value;
	return null;
}

function getElementConfig(el) {
	if (!el.__vueClipboard) {
		el.__vueClipboard = {
			text: "",
			success: null,
			error: null,
			onClick: null,
		};
	}
	return el.__vueClipboard;
}

function ensureClickHandler(el) {
	const cfg = getElementConfig(el);
	if (cfg.onClick) return;

	cfg.onClick = async () => {
		try {
			const e = await copyText(cfg.text);
			if (typeof cfg.success === "function") cfg.success(e);
		} catch (err) {
			if (typeof cfg.error === "function") cfg.error(err);
		}
	};

	el.addEventListener("click", cfg.onClick);
}

const clipboardDirective = {
	mounted(el, binding) {
		const cfg = getElementConfig(el);
		const arg = binding.arg || "copy";

		if (arg === "copy") {
			cfg.text = binding.value == null ? "" : binding.value;
			ensureClickHandler(el);
		} else if (arg === "success") {
			cfg.success = resolveHandler(binding);
			ensureClickHandler(el);
		} else if (arg === "error") {
			cfg.error = resolveHandler(binding);
			ensureClickHandler(el);
		}
	},
	updated(el, binding) {
		const cfg = getElementConfig(el);
		const arg = binding.arg || "copy";

		if (arg === "copy") {
			cfg.text = binding.value == null ? "" : binding.value;
		} else if (arg === "success") {
			cfg.success = resolveHandler(binding);
		} else if (arg === "error") {
			cfg.error = resolveHandler(binding);
		}
	},
	unmounted(el) {
		const cfg = el.__vueClipboard;
		if (cfg?.onClick) {
			el.removeEventListener("click", cfg.onClick);
		}
		delete el.__vueClipboard;
	},
};

export function installClipboard(app) {
	app.directive("clipboard", clipboardDirective);
	app.config.globalProperties.$copyText = (text) => copyText(text);
}

export default {
	install: installClipboard,
};
